import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { formatMoney, formatDate } from "../lib/format";
import { useConfirm, usePrompt } from "../components/Confirm";
import { CommentThread } from "../components/CommentThread";

const FORM_TYPE_LABELS: Record<string, { detailsTitle: string; startLabel: string; endLabel: string; calcButton: string; noun: string }> = {
  holiday:    { detailsTitle: "HOLIDAY DETAILS",    startLabel: "Holiday start date",    endLabel: "Holiday end date (return)",   calcButton: "Calculate Holiday Cost",    noun: "holiday" },
  hospital:   { detailsTitle: "HOSPITAL STAY DETAILS", startLabel: "Admission date",     endLabel: "Discharge date (return)",      calcButton: "Calculate Hospital Cost",   noun: "hospital stay" },
  transition: { detailsTitle: "TRANSITION DETAILS", startLabel: "Transition start date", endLabel: "Transition end date (return)", calcButton: "Calculate Transition Cost", noun: "transition" },
};

function TypeBadge({ type }: { type: string }) {
  const map: Record<string, { label: string; icon: string; classes: string }> = {
    holiday:     { label: "Holiday",      icon: "🏖", classes: "bg-liaise-blue/10 text-liaise-blue" },
    hospital:    { label: "Hospital",     icon: "🏥", classes: "bg-liaise-red/10 text-liaise-red" },
    transition:  { label: "Transition",   icon: "🤝", classes: "bg-liaise-green/15 text-emerald-800" },
    "case":      { label: "Case-by-case", icon: "📋", classes: "bg-liaise-purple/15 text-liaise-purple" },
    uplift:      { label: "Uplift",       icon: "📈", classes: "bg-liaise-yellow/30 text-amber-900" },
    awf:         { label: "AWF",          icon: "🎯", classes: "bg-liaise-orange/15 text-amber-900" },
  };
  const m = map[type] ?? { label: type, icon: "•", classes: "bg-slate-100 text-slate-700" };
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${m.classes}`}>
      <span>{m.icon}</span><span>{m.label}</span>
    </span>
  );
}

interface DayRow {
  day_date: string;
  day_index: number;
  is_return_day: boolean;
  incremental_day_hours: number;
  incremental_night_hours: number;
  sleep_in_shifts: number;
}

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export function HolidayDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const est = useQuery({ queryKey: ["est", id], queryFn: () => api.getEstimate(id) });
  const rates = useQuery({ queryKey: ["rates"], queryFn: api.rateCard });
  const homes = useQuery({ queryKey: ["homes"], queryFn: api.homes });
  const funders = useQuery({ queryKey: ["funders"], queryFn: api.funders });

  // editable copies of header fields
  const [pwsInitials, setPwsInitials] = useState("");
  const [homeId, setHomeId] = useState<number | "">("");
  const [funderId, setFunderId] = useState<number | "">("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [staffCount, setStaffCount] = useState(1);
  const [days, setDays] = useState<DayRow[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const confirm = useConfirm();
  const prompt = usePrompt();

  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const preparedByName = me.data?.name ?? est.data?.claimant_display_name ?? "";
  const preparedByJobTitle = me.data?.jobTitle ?? est.data?.job_title ?? "";
  const isCommercial = !!(me.data?.roles?.approver || me.data?.roles?.admin);

  const dateRangeInvalid = !!(startDate && endDate && endDate < startDate);
  const dateRangeMissing = !startDate || !endDate;
  const datesUsable = !dateRangeInvalid && !dateRangeMissing;
  const formLbl = FORM_TYPE_LABELS[est.data?.type ?? "holiday"] ?? FORM_TYPE_LABELS.holiday;

  useEffect(() => {
    if (!est.data) return;
    const e = est.data;
    setPwsInitials(e.pws_initials ?? "");
    setHomeId(e.home_id ?? "");
    setFunderId(e.funder_id ?? "");
    setStartDate(e.start_date?.slice(0, 10) ?? "");
    setEndDate(e.end_date?.slice(0, 10) ?? "");
    setStaffCount(e.staff_count ?? 1);
    // schedule
    if (e.days?.length) {
      setDays(e.days.map((d: any) => ({
        day_date: d.day_date?.slice(0, 10),
        day_index: d.day_index,
        is_return_day: !!d.is_return_day,
        incremental_day_hours: Number(d.incremental_day_hours),
        incremental_night_hours: Number(d.incremental_night_hours),
        sleep_in_shifts: Number(d.sleep_in_shifts),
      })));
    } else if (e.start_date && e.end_date) {
      const seed: DayRow[] = [];
      let i = 0;
      const s = new Date(e.start_date);
      const ed = new Date(e.end_date);
      for (let d = new Date(s); d <= ed; d.setDate(d.getDate() + 1)) {
        const iso = d.toISOString().slice(0, 10);
        const isReturn = iso === e.end_date.slice(0, 10);
        seed.push({
          day_date: iso, day_index: i++, is_return_day: isReturn,
          incremental_day_hours: 0, incremental_night_hours: 0, sleep_in_shifts: 0,
        });
      }
      setDays(seed);
    }
  }, [est.data]);

  // Re-seed days when dates change locally (only on draft)
  useEffect(() => {
    if (!est.data || est.data.status !== "draft") return;
    if (!startDate || !endDate) return;
    if (est.data.start_date?.slice(0, 10) === startDate && est.data.end_date?.slice(0, 10) === endDate) return;
    if (endDate < startDate) return; // invalid range, leave existing days alone until user fixes the dates
    const seed: DayRow[] = [];
    let i = 0;
    const s = new Date(startDate);
    const ed = new Date(endDate);
    for (let d = new Date(s); d <= ed; d.setDate(d.getDate() + 1)) {
      const iso = d.toISOString().slice(0, 10);
      const isReturn = iso === endDate;
      const existing = days.find((dx) => dx.day_date === iso);
      seed.push(existing ? { ...existing, day_index: i++, is_return_day: isReturn } : {
        day_date: iso, day_index: i++, is_return_day: isReturn,
        incremental_day_hours: 0, incremental_night_hours: 0, sleep_in_shifts: 0,
      });
    }
    setDays(seed);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  // Prefer the estimate-scoped rates (set on every CALCULATE; frozen on submit).
  // Fall back to the live rate card for brand-new drafts that have never been
  // calculated, and as a placeholder while est.data is still loading.
  const r = est.data?.rates ?? rates.data?.rates ?? {};
  const dayRate     = (r.day_hour?.pence ?? 0) / 100;
  const nightRate   = (r.night_hour?.pence ?? 0) / 100;
  const sleepRate   = (r.sleep_in?.pence ?? 0) / 100;
  const expensesRate = (r.staff_expense_per_day?.pence ?? 0) / 100;
  // Acuity-based suggestion removed 2026-05-20 per James. The Rules page still exposes
  // the suggested_acuity_* policies if Finance wants to re-enable it later.

  const totals = useMemo(() => {
    let dayHrs = 0, nightHrs = 0, sleeps = 0;
    for (const d of days) {
      dayHrs += d.incremental_day_hours;
      if (!d.is_return_day) {
        nightHrs += d.incremental_night_hours;
        sleeps += d.sleep_in_shifts;
      }
    }
    // Per Nick's 2026-05-27 ask: staff count only multiplies Staff Expenses.
    // Day, night and sleep-in costs reflect a single carer's hours regardless of
    // staff count (e.g. 4 hrs of day support with 2 staff is still 4 hrs at the
    // hourly rate, not 8). Expenses are the only line where headcount matters.
    const dayCost   = dayHrs   * dayRate;
    const nightCost = nightHrs * nightRate;
    const sleepCost = sleeps   * sleepRate;
    const daysCount = days.length;
    const expensesCost = daysCount * expensesRate * staffCount;
    const staffingTotal = dayCost + nightCost + sleepCost;
    const total = staffingTotal + expensesCost;
    return { dayHrs, nightHrs, sleeps, dayCost, nightCost, sleepCost, daysCount, expensesCost, staffingTotal, total };
  }, [days, dayRate, nightRate, sleepRate, expensesRate, staffCount]);

  if (est.isLoading) return <div className="text-slate-500">Loading...</div>;
  if (!est.data) return <div className="text-red-600">Not found.</div>;
  const e = est.data;
  const editable = e.status === "draft";

  async function saveAll() {
    await api.updateEstimate(id, {
      pws_initials: pwsInitials || null,
      home_id: homeId === "" ? null : homeId,
      funder_id: funderId === "" ? null : funderId,
      start_date: startDate || null,
      end_date: endDate || null,
      staff_count: staffCount,
      job_title: preparedByJobTitle || null,
    });
    await api.replaceDays(id, days);
    await qc.invalidateQueries({ queryKey: ["est", id] });
  }
  function hasAdditionalSupport(): boolean {
    return days.some((d) => d.incremental_day_hours > 0 || d.incremental_night_hours > 0 || d.sleep_in_shifts > 0);
  }
  async function calculate() {
    if (!hasAdditionalSupport()) {
      const ok = await confirm({
        title: "No additional support entered",
        body: "Your schedule has zero day hours, night hours and sleep-ins for every day. Calculating will only include staff expenses. If that is deliberate (rare), continue. Otherwise add hours to the rows above.",
        confirmLabel: "Calculate anyway",
        cancelLabel: "Go back and add hours",
        warning: true,
      });
      if (!ok) return;
    }
    await saveAll();
    setShowPreview(true);
  }
  async function submit() {
    if (!hasAdditionalSupport()) {
      const ok = await confirm({
        title: "Submit with no additional support?",
        body: "James will see a £0-staffing estimate (just staff expenses). Once approved, this is what the funder receives. If you meant to leave hours blank for this PWS, submit. Otherwise add hours first.",
        confirmLabel: "Submit anyway",
        cancelLabel: "Go back and add hours",
        warning: true,
      });
      if (!ok) return;
    }
    await saveAll();
    await api.estimateAction(id, "submit");
    navigate("/");
  }
  async function recall() {
    const r = await prompt({
      title: "Recall this estimate?",
      body: "It will go back to draft for editing. Let James know why so the audit is clear.",
      confirmLabel: "Recall",
      cancelLabel: "Keep submitted",
      warning: true,
      prompt: { label: "Reason for recall", placeholder: "e.g. funder updated dates", required: false, multiline: true },
    });
    if (!r.ok) return;
    await api.estimateAction(id, "recall", { reason: r.value || null });
    await qc.invalidateQueries({ queryKey: ["est", id] });
  }
  async function deleteDraft() {
    const ok = await confirm({
      title: "Delete this draft?",
      body: "The estimate, its schedule, and any adjustments will be removed. This cannot be undone.",
      confirmLabel: "Delete draft",
      cancelLabel: "Keep draft",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteEstimate(id);
    } catch (err) {
      alert((err as Error).message);
      return;
    }
    qc.removeQueries({ queryKey: ["est", id] });
    qc.invalidateQueries({ queryKey: ["my-estimates"] });
    navigate("/", { replace: true });
  }

  const homeName = (homes.data?.homes ?? []).find((h: any) => h.id === Number(homeId))?.name ?? e.home_name ?? "-";
  const funderName = (funders.data?.funders ?? []).find((f: any) => f.id === Number(funderId))?.name ?? e.funder_name ?? "-";

  return (
    <div className="space-y-6">
      {/* header */}
      <div className="flex items-baseline justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-liaise-navy">{e.request_number}</h1>
            <TypeBadge type={e.type ?? "holiday"} />
          </div>
          <div className="text-sm text-slate-500 mt-1 capitalize">{e.status}</div>
        </div>
        <Link to="/" className="text-sm text-slate-500 hover:underline">← Back</Link>
      </div>

      {e.status_reason && (
        <div className="text-sm px-4 py-3 rounded-xl bg-liaise-red/10 text-red-700">
          <b>Reason:</b> {e.status_reason}
        </div>
      )}

      {/* FIXED RATES banner */}
      <div className="rounded-2xl bg-liaise-navy text-white p-5">
        <div className="text-xs tracking-wider opacity-80 mb-3">FIXED RATES</div>
        <div className="flex flex-wrap gap-2">
          <Pill>Additional Day Support, {formatMoney(dayRate).slice(0)} / hr</Pill>
          <Pill>Additional Night Support, {formatMoney(nightRate)} / hr</Pill>
          <Pill>Sleep-in Shift, {formatMoney(sleepRate)} / shift</Pill>
          <Pill>Staff Expenses, {formatMoney(expensesRate)} / day / staff</Pill>
        </div>
      </div>

      {/* Section 1: Reference details */}
      <Section number={1} title="REFERENCE DETAILS">
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="PWS initials">
            <input className="input" maxLength={20} value={pwsInitials} disabled={!editable}
              onChange={(e) => setPwsInitials(e.target.value)} placeholder="JS" />
          </Field>
          <Field label="PWS home">
            <select className="input" value={homeId} disabled={!editable}
              onChange={(e) => setHomeId(e.target.value === "" ? "" : Number(e.target.value))}>
              <option value="">Select a home...</option>
              {(homes.data?.homes ?? []).map((h: any) => <option key={h.id} value={h.id}>{h.name}</option>)}
            </select>
          </Field>
          <Field label="Funder">
            <select className="input" value={funderId} disabled={!editable}
              onChange={(e) => setFunderId(e.target.value === "" ? "" : Number(e.target.value))}>
              <option value="">Select a funder...</option>
              {(funders.data?.funders ?? []).map((f: any) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </Field>
          <Field label="Prepared by">
            <div className="input bg-slate-50 text-slate-700 cursor-not-allowed">
              {preparedByName || "-"}
              {preparedByJobTitle && <div className="text-xs text-slate-500 mt-0.5">{preparedByJobTitle}</div>}
            </div>
          </Field>
        </div>
      </Section>

      {/* Section 2: type-specific details */}
      <Section number={2} title={formLbl.detailsTitle}>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label={formLbl.startLabel}>
            <input type="date" className="input" value={startDate} disabled={!editable}
              onChange={(e) => setStartDate(e.target.value)} />
          </Field>
          <Field label={formLbl.endLabel}>
            <input type="date" className="input" value={endDate} disabled={!editable}
              onChange={(e) => setEndDate(e.target.value)} />
          </Field>
          <Field label="No. of staff">
            <input type="number" min={1} className="input w-32" value={staffCount} disabled={!editable}
              onChange={(e) => setStaffCount(Math.max(1, Number(e.target.value)))} />
            <div className="text-xs text-slate-400 mt-1">
              Drives Staff Expenses only ({staffCount} staff &times; {formatMoney(expensesRate)}/day per day). Day, night and sleep-in costs are independent of headcount.
            </div>
          </Field>
        </div>
        {dateRangeInvalid && (
          <div className="mt-4 px-4 py-3 rounded-xl bg-liaise-red/10 text-red-700 text-sm">
            ⚠ End date is before the start date. Adjust the dates above so the return is on or after the start.
          </div>
        )}
      </Section>

      {/* Section 3: Daily support schedule */}
      <Section number={3} title="DAILY SUPPORT SCHEDULE">
        <div className="text-xs text-slate-500 mb-4 space-y-1">
          <div className="text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <b>Important:</b> The below should only include hours <b>in addition to</b> existing 1:1 and 2:1 support.
          </div>
          <div>Enter additional hours and sleep-in shifts for each day. Use 0.5 increments for hours. Night hours and sleep-ins are disabled on the return day.</div>
        </div>

        {!datesUsable && (
          <div className="px-4 py-3 rounded-xl bg-slate-100 text-slate-600 text-sm">
            {dateRangeInvalid
              ? "Fix the dates in section 2 above. End must be on or after start."
              : "Set both a start and end date in section 2 above. The day-by-day rows will appear here automatically."}
          </div>
        )}

        {/* Acuity-based suggestion removed 2026-05-20 per James — claimants own the hour figures
            and James reviews against commissioned hours at approval time. */}
        <div className="bg-liaise-navy text-white grid grid-cols-[1fr_140px_140px_140px] text-xs uppercase tracking-wide font-medium">
          <div className="px-4 py-3">Date</div>
          <div className="px-4 py-3">Add. day hrs<br/><span className="opacity-70 font-normal">@{formatMoney(dayRate)}/hr</span></div>
          <div className="px-4 py-3">Add. night hrs<br/><span className="opacity-70 font-normal">@{formatMoney(nightRate)}/hr</span></div>
          <div className="px-4 py-3">Sleep-in shifts<br/><span className="opacity-70 font-normal">@{formatMoney(sleepRate)}/shift</span></div>
        </div>
        {days.map((d, i) => (
          <div key={i} className="grid grid-cols-[1fr_140px_140px_140px] items-center border-b border-slate-100 hover:bg-slate-50">
            <div className="px-4 py-3">
              <div className="text-sm font-semibold text-liaise-navy">{weekdayName(d.day_date)}</div>
              <div className="text-xs text-slate-500">{formatDate(d.day_date)}{d.is_return_day ? <span className="italic"> (return)</span> : null}</div>
            </div>
            <div className="px-4 py-2">
              <NumberCell value={d.incremental_day_hours} disabled={!editable}
                onChange={(v) => setDays(updateAt(days, i, { incremental_day_hours: v }))} />
            </div>
            <div className="px-4 py-2">
              <NumberCell value={d.incremental_night_hours} disabled={!editable || d.is_return_day}
                onChange={(v) => setDays(updateAt(days, i, { incremental_night_hours: v }))} />
            </div>
            <div className="px-4 py-2">
              <NumberCell value={d.sleep_in_shifts} step={1} disabled={!editable || d.is_return_day}
                onChange={(v) => setDays(updateAt(days, i, { sleep_in_shifts: v }))} />
            </div>
          </div>
        ))}
      </Section>

      {/* Calculate / Submit buttons */}
      <div className="flex flex-col sm:flex-row gap-3 items-center no-print">
        {editable && (
          <>
            <button onClick={calculate} disabled={!datesUsable}
              className="px-6 py-3 rounded-lg bg-liaise-green text-white text-sm font-semibold tracking-wide uppercase shadow-sm hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed">
              {formLbl.calcButton}
            </button>
            <button onClick={saveAll} className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-sm">Save draft</button>
            <button onClick={submit} disabled={!datesUsable}
              className="px-4 py-2 rounded-lg bg-liaise-navy text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed">
              Submit for approval
            </button>
            <button onClick={deleteDraft} className="px-4 py-2 rounded-lg text-sm text-red-600 hover:underline">Delete draft</button>
            {!datesUsable && <span className="text-xs text-slate-500">Set valid dates first.</span>}
          </>
        )}
        {e.status === "submitted" && (
          <button onClick={recall} className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-sm">Recall</button>
        )}
        {(e.status === "rejected" || e.status === "recalled") && (
          <button
            onClick={async () => {
              await api.estimateAction(id, "reopen");
              await qc.invalidateQueries({ queryKey: ["est", id] });
            }}
            className="px-4 py-2 rounded-lg bg-liaise-navy text-white text-sm font-medium">
            Reopen and edit
          </button>
        )}
        {e.status === "approved" && (
          <Link to={`/estimate/${id}/print`} className="px-4 py-2 rounded-lg bg-liaise-navy text-white text-sm font-medium">
            Print / save as PDF →
          </Link>
        )}
      </div>

      {/* Live result card (mirrors the print/email layout) */}
      {(showPreview || !editable) && (
        <EstimateOutput
          status={e.status}
          type={e.type}
          requestNumber={e.request_number}
          pwsInitials={pwsInitials}
          homeName={homeName}
          funderName={funderName}
          preparedBy={preparedByName}
          preparedByJobTitle={preparedByJobTitle}
          startDate={startDate}
          endDate={endDate}
          staffCount={staffCount}
          totals={totals}
          dayRate={dayRate}
          nightRate={nightRate}
          sleepRate={sleepRate}
          expensesRate={expensesRate}
          adjustments={est.data?.adjustments ?? []}
        />
      )}

      <CommentThread estimateId={id!} currentOid={me.data?.oid ?? ""} isAdmin={!!me.data?.roles?.admin} />

      <style>{`.input{display:block;width:100%;padding:.5rem .75rem;border:1px solid rgb(226 232 240);border-radius:.5rem;font-size:.875rem;background:white}`}</style>
    </div>
  );
}

function Section({ number, title, children }: { number: number; title: string; children: React.ReactNode }) {
  return (
    <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100">
        <span className="w-8 h-8 rounded-full bg-liaise-navy text-white text-sm font-semibold flex items-center justify-center">{number}</span>
        <h2 className="text-sm font-semibold tracking-wider text-liaise-navy">{title}</h2>
      </div>
      <div className="p-6">{children}</div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-slate-500 mb-1 tracking-wider uppercase">{label}</div>
      {children}
    </label>
  );
}

function Pill({ children }: { children: React.ReactNode }) {
  return <span className="rounded-full bg-white/10 border border-white/15 px-3 py-1.5 text-xs font-medium">{children}</span>;
}

function NumberCell({ value, onChange, disabled, step = 0.5 }: {
  value: number; onChange: (n: number) => void; disabled?: boolean; step?: number;
}) {
  return (
    <input type="number" step={step} min={0} disabled={disabled} value={value}
      onChange={(e) => onChange(Number(e.target.value || 0))}
      className="w-full text-center text-sm border border-slate-200 rounded px-3 py-2 disabled:bg-slate-100 disabled:text-slate-400" />
  );
}

function updateAt<T>(arr: T[], i: number, patch: Partial<T>): T[] {
  const copy = [...arr];
  copy[i] = { ...copy[i], ...patch };
  return copy;
}

function deriveInitials(name?: string | null): string {
  if (!name) return "";
  return name.split(/\s+/).filter(Boolean).map((p) => p[0]).slice(0, 3).join("").toUpperCase();
}

function weekdayName(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return WEEKDAYS[d.getDay()];
}

/* --------- The output card shown after Calculate (and reused in print/email) ---------- */
const OUTPUT_TYPE_LABELS: Record<string, { title: string; noun: string; duration: string }> = {
  holiday:    { title: "Holiday staffing cost estimate",       noun: "holiday",       duration: "Holiday duration" },
  hospital:   { title: "Hospital-stay staffing cost estimate", noun: "hospital stay", duration: "Stay duration" },
  transition: { title: "Transition staffing cost estimate",    noun: "transition",    duration: "Transition window" },
};
export function EstimateOutput(props: {
  status?: string;
  type?: string;
  requestNumber?: string;
  pwsInitials: string;
  homeName: string;
  funderName: string;
  preparedBy: string;
  preparedByJobTitle?: string;
  startDate: string;
  endDate: string;
  staffCount: number;
  totals: { dayHrs: number; nightHrs: number; sleeps: number; dayCost: number; nightCost: number; sleepCost: number; daysCount: number; expensesCost: number; staffingTotal: number; total: number };
  dayRate: number; nightRate: number; sleepRate: number; expensesRate: number;
  /** Optional approver-added adjustments. Each entry can be positive (surcharge) or negative (discount).
   *  amount_pence is stored on the estimate; we apply them to the displayed grand total so the in-app
   *  preview matches the funder-facing email. */
  adjustments?: Array<{ amount_pence: number; reason?: string | null }>;
}) {
  const t = props.totals;
  const nights = Math.max(0, t.daysCount - 1);
  const adjustments = props.adjustments ?? [];
  const adjTotalPence = adjustments.reduce((s, a) => s + Number(a.amount_pence ?? 0), 0);
  const adjTotal = adjTotalPence / 100;
  const grandTotal = t.total + adjTotal;
  const status = props.status ?? "draft";
  const lbl = OUTPUT_TYPE_LABELS[props.type ?? "holiday"] ?? OUTPUT_TYPE_LABELS.holiday;
  const isApproved = status === "approved";
  const isSubmitted = status === "submitted";
  const isDraft = status === "draft";
  return (
    <div className="rounded-2xl border-2 overflow-hidden"
         style={{ borderColor: isApproved ? "#3DAE2B" : isSubmitted ? "#FF8300" : "#cbd5e1", background: "#ffffff" }}>
      {!isApproved && (
        <div className={`px-6 py-3 text-sm font-semibold uppercase tracking-wider flex items-center gap-2 ${
          isSubmitted ? "bg-liaise-orange/15 text-orange-800" : "bg-slate-100 text-slate-600"
        }`}>
          <span className="inline-block w-2 h-2 rounded-full bg-current animate-pulse" />
          {isDraft && "Preview, not yet submitted. Funder will only see this once approved."}
          {isSubmitted && "Submitted. Awaiting approval from Commercial Finance."}
        </div>
      )}
      {isApproved && (
        <div className="px-6 py-3 text-sm font-semibold uppercase tracking-wider bg-liaise-green/15 text-emerald-800 flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-emerald-600" />
          Approved, ready to forward to the funder.
        </div>
      )}
      <div className="p-8">
        <div className="flex items-start justify-between gap-4 border-b-2 border-liaise-navy pb-5 mb-5">
          <img src="/liaise-wordmark-navy.svg" alt="Liaise" className="h-9 w-auto" />
          <div className="text-right">
            <div className="text-xs tracking-wider text-slate-500 uppercase">{lbl.title}</div>
            <div className="text-xl font-semibold text-liaise-navy mt-1">
              {formatDate(props.startDate)} – {formatDate(props.endDate)}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-4 gap-x-6 mb-6 text-sm">
          <Meta label="PWS initials"      value={props.pwsInitials || "-"} />
          <Meta label="PWS home"          value={props.homeName} />
          <Meta label="Funder"            value={props.funderName} />
          <Meta label={lbl.duration}  value={`${t.daysCount} day${t.daysCount === 1 ? "" : "s"} / ${nights} night${nights === 1 ? "" : "s"}`} />
          <Meta label="Staff attending"   value={`${props.staffCount} member${props.staffCount === 1 ? "" : "s"}`} />
          <Meta label="Prepared by"       value={
            <>
              {props.preparedBy || "-"}
              {props.preparedByJobTitle && <div className="text-xs font-normal text-slate-500 mt-1">{props.preparedByJobTitle}</div>}
            </>
          } />
        </div>

        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-liaise-navy text-white text-left">
              <th className="px-4 py-3 font-semibold">Description</th>
              <th className="px-4 py-3 font-semibold text-right">Quantity</th>
              <th className="px-4 py-3 font-semibold text-right">Rate</th>
              <th className="px-4 py-3 font-semibold text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            <SubHeader text="Staffing costs" />
            <CostRow label="Additional Day Support"   qty={`${t.dayHrs} hrs`}   rate={`${formatMoney(props.dayRate)}/hr`}     total={t.dayCost} />
            <CostRow label="Additional Night Support" qty={`${t.nightHrs} hrs`} rate={`${formatMoney(props.nightRate)}/hr`}   total={t.nightCost} />
            <CostRow label="Sleep-in Shifts"           qty={`${t.sleeps} shifts`} rate={`${formatMoney(props.sleepRate)}/shift`} total={t.sleepCost} />
            <SubHeader text="Additional costs" />
            <CostRow
              label="Staff Expenses"
              sub={`${props.staffCount} staff × ${t.daysCount} day${t.daysCount === 1 ? "" : "s"} × ${formatMoney(props.expensesRate)}/day`}
              qty={`${t.daysCount} days`}
              rate={`${formatMoney(props.expensesRate)}/day`}
              total={t.expensesCost}
            />
            {adjustments.length > 0 && <SubHeader text="Adjustment" />}
            {adjustments.map((a, idx) => (
              <CostRow
                key={idx}
                label={a.reason ? a.reason : "Adjustment"}
                qty=""
                rate=""
                total={Number(a.amount_pence ?? 0) / 100}
              />
            ))}
            <tr className="bg-liaise-navy text-white">
              <td className="px-4 py-3 font-semibold uppercase tracking-wider text-sm" colSpan={3}>Total estimated {lbl.noun} cost</td>
              <td className="px-4 py-3 font-semibold text-right tabular-nums">{formatMoney(grandTotal)}</td>
            </tr>
          </tbody>
        </table>

        <p className="text-xs text-slate-500 mt-5 leading-relaxed">
          <b>Note:</b> All rates are fixed and non-negotiable. This estimate covers additional staffing costs only and is based on the support schedule entered above. Figures are exclusive of VAT unless otherwise stated. Subject to formal confirmation. Prepared using the Liaise Pricing tool.
        </p>
      </div>
    </div>
  );
}

function Meta({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className="text-sm font-semibold text-liaise-navy mt-1">{value}</div>
    </div>
  );
}

function SubHeader({ text }: { text: string }) {
  return (
    <tr className="bg-slate-50">
      <td colSpan={4} className="px-4 py-2 text-xs uppercase tracking-wider font-semibold text-liaise-navy text-right">{text}</td>
    </tr>
  );
}

function CostRow({ label, sub, qty, rate, total }: { label: string; sub?: string; qty: string; rate: string; total: number }) {
  return (
    <tr className="border-b border-slate-100">
      <td className="px-4 py-3">
        <div>{label}</div>
        {sub && <div className="text-xs text-slate-500">{sub}</div>}
      </td>
      <td className="px-4 py-3 text-right text-slate-700">{qty}</td>
      <td className="px-4 py-3 text-right text-slate-700">{rate}</td>
      <td className="px-4 py-3 text-right font-semibold tabular-nums">{formatMoney(total)}</td>
    </tr>
  );
}
